import {Autocomplete, debounce} from '@mui/material';
import TextField from '@mui/material/TextField';
import {useField, useFormikContext} from 'formik';
import {isEmpty, notEmpty} from 'main/utility/CommonUtil';
import PropTypes from 'prop-types';
import {useEffect, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {getWidgetFieldOptions} from 'redux/actions';
import {MINSEARCHCHAR} from 'shared/constants/UiWiseConst';

const EMPTY_OPTIONS = [];

const AppAutocompleteTextField = (props) => {
  const {widgetid, minsearchchar = MINSEARCHCHAR, name} = props;
  const [field, meta] = useField(name);
  const {setFieldValue} = useFormikContext();
  const dispatch = useDispatch();

  const errorText = meta.error && meta.touched ? meta.error : '';
  const fieldOptions = useSelector(
    ({widgetReducer}) =>
      widgetReducer.widgetFieldOptionsById?.[widgetid]?.[name] || EMPTY_OPTIONS,
  );

  // 1. Initial Fetch
  useEffect(() => {
    if (isEmpty(fieldOptions) && notEmpty(field.value)) {
      dispatch(getWidgetFieldOptions(widgetid, name, {search: field.value}));
    }
  }, [dispatch, widgetid, name, field.value]);

  // 2. Search Logic
  const debouncedSearch = useMemo(
    () =>
      debounce((searchValue) => {
        const sanitized = encodeURIComponent(searchValue.replace(/'/g, "''"));
        dispatch(
          getWidgetFieldOptions(widgetid, name, {
            search: sanitized,
          }),
        );
      }, 500), // 500ms wait for typing to pause
    [dispatch, widgetid, name],
  );

  const handleSearch = (newInputValue) => {
    if (newInputValue.length >= minsearchchar) {
      debouncedSearch(newInputValue);
    }
  };

  const handleInputChange = (newInputValue, reason) => {
    if (reason === 'input') {
      handleSearch(newInputValue);
    }
  };

  const handleChange = (newValue) => {
    if (newValue) setFieldValue(field.name, newValue);
  };

  const optionValueMatch = (option, value) => {
    if (value === '' || value === null || value === undefined) return true;
    const optionVal = typeof option === 'object' ? option.value : option;
    const compareVal = typeof value === 'object' ? value.value : value;
    return optionVal === compareVal;
  };

  return (
    <Autocomplete
      fullWidth
      autoSelect
      {...field}
      options={fieldOptions}
      value={field.value || ''}
      isOptionEqualToValue={(option, value) => optionValueMatch(option, value)}
      getOptionLabel={(option) => {
        if (typeof option === 'string') return option;
        return option?.label || '';
      }}
      noOptionsText={field.value ? 'Not found' : 'Search...'}
      onInputChange={(_, newInputValue, reason) =>
        handleInputChange(newInputValue, reason)
      }
      onChange={(_, newValue) => handleChange(newValue)}
      onBlur={(_, newValue) => handleChange(newValue)}
      renderInput={(params) => (
        <TextField
          {...params}
          {...props}
          helperText={errorText}
          error={!!errorText}
        />
      )}
    />
  );
};

export default AppAutocompleteTextField;

AppAutocompleteTextField.propTypes = {
  widgetid: PropTypes.string,
  name: PropTypes.string,
  minsearchchar: PropTypes.number,
};
